import { useEffect, useState } from 'react';
import customFetch from '@utils/customFetch';
import useDebouncedValue from '@/hooks/useDebouncedValue';

/**
 * Benutzersuche mit Debounce für die Admin-Masken.
 *
 * Ersetzt die rohen Benutzer-ID-Felder (Kartenadministration) und die zweimal
 * kopierte Suchlogik in NewManualOrder. Eine eingefügte Benutzer-ID
 * funktioniert weiterhin: der Server matcht `search` auch gegen die _id, der
 * Treffer erscheint dann mit Name und E-Mail in der Liste und wird bewusst
 * bestätigt — statt eine ID ungeprüft zu speichern.
 */
const UserSearchSelect = ({
  selectedUser,
  onSelect,
  label = 'Benutzer',
  placeholder = 'Name, E-Mail oder Benutzer-ID …',
  helperText,
  allowClear = false,
  clearLabel = 'Besitzer entfernen',
  autoFocusOnEdit = true,
}) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSearching, setIsSearching] = useState(!selectedUser);
  const debouncedTerm = useDebouncedValue(term, 300);

  useEffect(() => {
    const query = debouncedTerm.trim();
    if (query.length < 2) {
      setResults([]);
      setError('');
      return undefined;
    }

    // Antworten einer bereits überholten Eingabe dürfen das Ergebnis nicht
    // mehr überschreiben.
    let cancelled = false;
    setLoading(true);

    customFetch
      .get(
        `/users/admin/all-users?search=${encodeURIComponent(query)}&limit=8`
      )
      .then(({ data }) => {
        if (cancelled) return;
        setResults(data?.users ?? []);
        setError('');
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setError('Suche fehlgeschlagen. Bitte nochmals versuchen.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedTerm]);

  const handleSelect = (user) => {
    onSelect(user);
    setTerm('');
    setResults([]);
    setIsSearching(false);
  };

  const handleClear = () => {
    onSelect(null);
    setTerm('');
    setResults([]);
    setIsSearching(true);
  };

  if (selectedUser && !isSearching) {
    return (
      <div>
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        <div className="mt-1 flex items-start justify-between gap-3 rounded-md bg-white px-3 py-2 ring-1 ring-inset ring-gray-300">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-gray-900">
              {selectedUser.firstName} {selectedUser.lastName}
            </p>
            <p className="truncate text-sm text-gray-500">
              {selectedUser.email}
            </p>
            <p className="mt-0.5 truncate text-xs text-gray-400">
              ID: {selectedUser._id}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <button
              type="button"
              onClick={() => setIsSearching(true)}
              className="text-sm font-semibold text-svisit-primary-600 hover:text-svisit-primary-500 hover:cursor-pointer"
            >
              Ändern
            </button>
            {allowClear && (
              <button
                type="button"
                onClick={handleClear}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 hover:cursor-pointer"
              >
                {clearLabel}
              </button>
            )}
          </div>
        </div>
        {helperText && (
          <p className="mt-1 text-xs text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <label
        htmlFor="user-search-select"
        className="block text-sm font-medium text-gray-900"
      >
        {label}
      </label>
      <div className="relative mt-1">
        <input
          id="user-search-select"
          type="text"
          autoComplete="off"
          autoFocus={autoFocusOnEdit && Boolean(selectedUser)}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={placeholder}
          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
        />

        {results.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
            {results.map((user) => (
              <li key={user._id}>
                <button
                  type="button"
                  onClick={() => handleSelect(user)}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 hover:cursor-pointer"
                >
                  <span className="block font-medium text-gray-900">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="block text-gray-500">{user.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-1 flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          {loading
            ? 'Suche läuft …'
            : error ||
              (debouncedTerm.trim().length >= 2 && results.length === 0
                ? 'Keine Treffer.'
                : helperText || 'Ab zwei Zeichen wird gesucht.')}
        </p>
        {selectedUser && (
          <button
            type="button"
            onClick={() => {
              setTerm('');
              setResults([]);
              setIsSearching(false);
            }}
            className="shrink-0 text-xs font-medium text-gray-500 hover:text-gray-700 hover:cursor-pointer"
          >
            Abbrechen
          </button>
        )}
      </div>
    </div>
  );
};

export default UserSearchSelect;
